import CreateAccountForm from '../../../components/forms/create-account/create-account.form';
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { getCivilities, getInterlocutorsTypes } from '../../../services/references.service';
import { ReferenceDto } from '../../../services/dtos/reference.dto';
import { useSnackbars } from '../../../hooks/snackbar.hook';
import { PasswordCriterionsDto } from '../../../services/dtos/password-criterions.dto';
import { createAccount, getPasswordCriterions, isValidPassword } from '../../../services/account.service';
import { AxiosError } from 'axios';
import { SubscriberAccountDto } from '../../../services/dtos/subscriber-account.dto';
import { useNavigate } from 'react-router-dom';
import { InterlocutorsReferenceDto } from '../../../services/dtos/interlocutors-reference.dto';
import { cloneObjectWithoutNullAndEmptyValue } from '../../../services/http-core-service';
import useRecaptcha from '../../../hooks/google-recaptcha.hook';
import { env } from '../../../env';
import { showAxiosMessageError } from '../../../services/error-message.service';

export default function CreateAccountPage() {
    //Navigate
    const navigate = useNavigate();
    //Permet de savoir si on est passé une fois dans l'useEffect
    const dataFetchedRef = useRef(false);
    //SnackBar pour les messages
    const showMessage = useSnackbars();

    //Références
    const [civilities, setCivilities] = useState<ReferenceDto[]>([]);
    const [interlocutors, setInterlocutors] = useState<InterlocutorsReferenceDto[]>([]);
    const [messagesPassword, setMessagesPassword] = useState<string[]>([]);
    const [validate, setValidate] = useState(false);
    const [isMoral, setIsMoral] = React.useState(false);

    //Gestion du mot de passe
    const [passwordCriterions, setPasswordCriterions] = useState<PasswordCriterionsDto | null>(null);

    //Recaptcha
    const { getRecaptchaToken } = useRecaptcha('creation_compte');

    useEffect(() => {
        if (dataFetchedRef.current) {
            return;
        }
        dataFetchedRef.current = true;

        //Appel des civilités
        getCivilities()
            .then(result => {
                const data: ReferenceDto[] =
                    result &&
                    result.map(e => {
                        return {
                            value: e.id,
                            label: e.nom,
                        };
                    });
                setCivilities(data);
            })
            .catch((error: Error | AxiosError) => {
                showAxiosMessageError(error, showMessage);
            });

        //Appel types d'interlocuteurs
        getInterlocutorsTypes()
            .then(result => {
                const data: InterlocutorsReferenceDto[] =
                    result &&
                    result.map(e => {
                        return {
                            id: e.id,
                            label: e.nom,
                            moralPerson: e.personneMorale,
                        };
                    });
                setInterlocutors(data);
            })
            .catch((error: Error | AxiosError) => {
                showAxiosMessageError(error, showMessage);
            });

        //Appel des critères pour les mots de passe
        getPasswordCriterions()
            .then(result => {
                //Ajout le message pour la majuscule
                if (result.upperCase) {
                    setMessagesPassword(oldArray => [...oldArray, 'une lettre en majuscule']);
                }
                //Ajout le message pour la minuscule
                if (result.lowerCase) {
                    setMessagesPassword(oldArray => [...oldArray, 'une lettre en minuscule']);
                }
                //Ajout le message pour le chiffre
                if (result.number) {
                    setMessagesPassword(oldArray => [...oldArray, 'un chiffre']);
                }
                //Ajout le message pour le caractère spécifique
                if (result.specialChars) {
                    setMessagesPassword(oldArray => [...oldArray, 'un caractère spécifique']);
                }
                //Ajout le message pour la taille
                if (result.maxLength !== undefined && result.minLength !== undefined) {
                    setMessagesPassword(oldArray => [
                        ...oldArray,
                        'au minimum ' + result.minLength + ' caractères et maximum ' + result.maxLength,
                    ]);
                }
                setPasswordCriterions(result);
            })
            .catch((error: Error | AxiosError) => {
                showAxiosMessageError(error, showMessage);
            });

        //https://stackoverflow.com/questions/55840294/how-to-fix-missing-dependency-warning-when-using-useeffect-react-hook
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handlerOnChangePassword = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        e.preventDefault();
        if (e?.target?.value.length > 0) {
            //Appel des civilités
            isValidPassword(e?.target?.value)
                .then(() => {
                    showMessage.infoAlert('Mot de passe validé');
                })
                .catch((error: Error | AxiosError) => {
                    showAxiosMessageError(error, showMessage);
                });
        }
    };

    //Handler submit form
    const handlerSubmit = (e: SubscriberAccountDto) => {
        // Empêche de pouvoir submit pendant le traitement
        setValidate(true);
        //Mapping des données non présentes dans le formulaire.
        //Civilité : on doit mettre un "lien"
        //type 'interlocuteur' : on doit mettre un "lien"
        let subscriberAccountDto: SubscriberAccountDto = {
            ...e,
            urlValidation: env.REACT_APP_URL_VALIDATION_ACCOUNT,
            utilisateur: {
                ...e.utilisateur,
                civilite: isMoral ? undefined : '/civilites/' + e.utilisateur.civilite,
                type: '/interlocuteur_types/' + e.utilisateur.type,
                telPort: e.utilisateur.telPort === '' ? undefined : '0' + e.utilisateur.telPort,
                telFixe: e.utilisateur.telFixe === '' ? undefined : '0' + e.utilisateur.telFixe,
                mailPerso: e.utilisateur.mailPerso === '' ? e.identifiant : e.utilisateur.mailPerso,
            },
        };

        //Suppression des champs vides ou null
        subscriberAccountDto = {
            ...subscriberAccountDto,
            utilisateur: cloneObjectWithoutNullAndEmptyValue(subscriberAccountDto.utilisateur),
        };

        //Récupération du recaptcha
        getRecaptchaToken()
            .then(token => {
                //On met à jour le recaptcha
                subscriberAccountDto = {
                    ...subscriberAccountDto,
                    captcha: token,
                };
                createAccount(subscriberAccountDto)
                    .then(() => {
                        showMessage.successAlert(
                            'Enregistrement du compte réussi : vous allez recevoir un mail de confirmation prochainement'
                        );
                        navigate('/');
                    })
                    .catch((error: Error | AxiosError) => {
                        showAxiosMessageError(error, showMessage);
                    })
                    .finally(() => {
                        setValidate(false);
                    });
            })
            .catch((error: Error | AxiosError) => {
                showAxiosMessageError(error, showMessage);
            });
    };

    /**
     * Changement de la variable isMoral au choix de l'interlocuteur (a une influence sur les champs affichés dans le formulaire
     * @param e déclencheur de l'évènement
     */
    const handleChangeInterlocutor = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        e.preventDefault();
        const selectedInterlocutor = interlocutors.find(interlocutor => interlocutor.id === e?.target?.value);
        setIsMoral(selectedInterlocutor?.moralPerson === undefined ? false : selectedInterlocutor.moralPerson);
    };

    /**
     * Mapping des interlocuteurs pour la liste du formulaire
     */
    const interlocutorsSelection = interlocutors.map(interlocutor => {
        return {
            value: interlocutor.id,
            label: interlocutor.label,
        };
    });

    return (
        // retourne la vue "CreateAccountForm"
        <CreateAccountForm
            handlerSubmit={handlerSubmit}
            handlerOnChangePassword={handlerOnChangePassword}
            civilities={civilities}
            passwordCriterions={passwordCriterions}
            messagesPassword={messagesPassword}
            isMoral={isMoral}
            validate={validate}
            handleChangeInterlocutor={handleChangeInterlocutor}
            interlocutorsSelection={interlocutorsSelection}
        />
    );
}
